/**
 * ByokAddKeyDialog — guided form for storing a BYOK API key.
 *
 * The key value is held in component state only until submit. After a
 * successful RPC we drop the value and close. We never log it, never put it
 * in a query cache, never echo it back from the network response.
 */
import * as React from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { STORABLE_PROVIDERS, getProviderInfo, type ByokProvider } from "@/theme/byok/providers";
import { useStoreByokKey, type ByokProviderEnum } from "@/theme/byok/useByokKeys";

const FormSchema = z.object({
  provider: z.enum([
    "openai",
    "anthropic",
    "google",
    "mistral",
    "groq",
    "openrouter",
    "custom",
  ]),
  label: z.string().min(1, "Label is required").max(64),
  apiKey: z.string().min(8, "Key looks too short").max(500),
});
type FormValues = z.infer<typeof FormSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ByokAddKeyDialog({ open, onOpenChange }: Props) {
  const store = useStoreByokKey();
  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { provider: "openai", label: "", apiKey: "" },
  });

  const provider = form.watch("provider") as ByokProvider;
  const info = getProviderInfo(provider);

  const onSubmit = async (values: FormValues) => {
    try {
      await store.mutateAsync({
        provider: values.provider as ByokProviderEnum,
        apiKey: values.apiKey,
        label: values.label.trim(),
      });
      toast.success("API key saved");
      form.reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save key";
      toast.error(msg);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) form.reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add API key</DialogTitle>
          <DialogDescription>
            Your key is encrypted at rest and is never sent to the browser
            after this form is submitted. It's only decrypted server-side when
            you generate a theme.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="byok-provider">Provider</Label>
            <Select
              value={form.watch("provider")}
              onValueChange={(v) => form.setValue("provider", v as FormValues["provider"])}
            >
              <SelectTrigger id="byok-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STORABLE_PROVIDERS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{info.blurb}</p>
            {info.helpUrl && (
              <a
                href={info.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Where do I find this key? <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="byok-label">Label</Label>
            <Input
              id="byok-label"
              placeholder="e.g. personal sandbox"
              {...form.register("label")}
            />
            {form.formState.errors.label && (
              <p className="text-xs text-destructive">
                {form.formState.errors.label.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="byok-apikey">API key</Label>
            <Input
              id="byok-apikey"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="sk-..."
              {...form.register("apiKey")}
            />
            {form.formState.errors.apiKey && (
              <p className="text-xs text-destructive">
                {form.formState.errors.apiKey.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={store.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={store.isPending}>
              {store.isPending ? "Saving..." : "Save key"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
